import { useMemo } from 'react';
import {
  toNewHomeSectionsPresentationVm,
  type NewHomeSectionPresentationVm,
} from '../viewModels/newHome.vm';
import type {
  FloatingNavConfig,
  MobileHeaderConfig,
  DesktopHeaderConfig,
  HomeSeoContentSectionPageValue,
} from '../../domain/models/NewHomePageData';
import type { NewHomePageAppResult } from '../../application/services/NewHomeService';
import type { PlpFiltersResult } from '@/features/plp/domain/models/ProductListing';
import type { RecommendedBrowsePageResult } from '../../application/services/NewHomeService';

interface NewHomeServiceInterface {
  getPageDataSync(): NewHomePageAppResult;
  getPageData(): Promise<NewHomePageAppResult>;
  getRecommendedProducts(
    tags: string,
    pageNumber: number,
    options?: { isFiltered?: boolean; signal?: AbortSignal }
  ): Promise<RecommendedBrowsePageResult>;
  fetchBrowseFilterPreview(params: {
    tags: string;
    tags_to_sort_filters?: string;
  }): Promise<PlpFiltersResult>;
}

export interface UseNewHomeServiceOptions {
  service: NewHomeServiceInterface;
}

export interface UseNewHomeServiceReturn {
  sections: NewHomeSectionPresentationVm[];
  floatingNav: FloatingNavConfig | null;
  mobileHeader: MobileHeaderConfig | null;
  desktopHeader: DesktopHeaderConfig | null;
  homeSeoContentSection: HomeSeoContentSectionPageValue | null;
  isLoading: boolean;
  error: string | null;
  getRecommendedProducts: (
    tags: string,
    pageNumber: number,
    options?: { isFiltered?: boolean; signal?: AbortSignal }
  ) => Promise<RecommendedBrowsePageResult>;
  fetchBrowseFilterPreview: (params: {
    tags: string;
    tags_to_sort_filters?: string;
  }) => Promise<PlpFiltersResult>;
}

/**
 * Page JSON is bundled locally (see newHomeAdapter). Read it synchronously so SSR matches old home:
 * first paint has sections without waiting for useEffect (which never runs on the server).
 */
export function useNewHomeService({ service }: UseNewHomeServiceOptions): UseNewHomeServiceReturn {
  const pageData = useMemo(() => {
    try {
      return service.getPageDataSync();
    } catch {
      return null;
    }
  }, [service]);

  const sections = useMemo(() => {
    if (!pageData?.sections) return [];
    return toNewHomeSectionsPresentationVm(
      pageData.sections as unknown as Array<{
        priority: number;
        key: string;
        eventLabel: string;
        showInMobile: boolean;
        showInDesktop: boolean;
        value: Record<string, unknown>;
      }>
    );
  }, [pageData]);

  const floatingNav = pageData?.floatingNav ?? null;
  const mobileHeader = pageData?.mobileHeader ?? null;
  const desktopHeader = pageData?.desktopHeader ?? null;
  const homeSeoContentSection = pageData?.homeSeoContentSection ?? null;
  return {
    sections,
    floatingNav,
    mobileHeader,
    desktopHeader,
    homeSeoContentSection,
    isLoading: !pageData,
    error: pageData ? null : 'Failed to load sections',
    getRecommendedProducts: service.getRecommendedProducts,
    fetchBrowseFilterPreview: service.fetchBrowseFilterPreview,
  };
}
